//@ts-nocheck
import { routingMixin } from "@jack-henry/web-component-router";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fixedEncodeURIComponent, getRequest, postRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import {
  applyInstitutionFieldErrors,
  hasInstitutionUuidValidationError,
} from "../../utils/institution-validation-errors";
import { requestInstitutionsListRefresh } from "../../utils/institution-list-refresh";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/forms/jha-form-text-input/jha-form-text-input";
import "@banno/jha-wc/src/forms/jha-form-checkbox/jha-form-checkbox";
import "@banno/jha-wc/src/forms/jha-form-row/jha-form-row";
import { decryptValue, showToastMessage } from "../../utils/app-utils";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { StatusConstants } from "../../utils/apiStatusConstants";
import { AddInstitutionType } from "../../types/BusinessUnits/AddInstitutionType";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import Cookies from "js-cookie";
import { AppRouters } from "../../utils/app-router";
import { GlobalVariables, UserTypes } from "../../utils/constants";
import { isSessionBusinessUnitAllowed } from "../../utils/business-unit-session";
import { FeatureFlags } from "../../utils/launchdarkly-config";
import { launchDarkly } from "../../utils/launchdarkly-service";
import { isInitialInstitutionFeatureEnabled } from "../../utils/initial-institution-feature-gate";

@customElement("add-institution")
export class AddInstitution extends routingMixin(LitElement) {
  static get is() {
    return "add-institution";
  }

  @property({ reflect: true, attribute: "business-unit-id" })
  businessUnitId = "";

  @property({ type: Object }) formData: AddInstitutionType = {
    institutionUuid: "",
    institutionRtId: "",
    institutionEnvironment: "",
    consumerNameProduct: "",
    businessUnitId: "",
    isDefault: false,
    createdBy: decryptValue(Cookies.get("userId") || ""),
  };

  @property({ type: Boolean }) apiLoading = false;
  @property({ type: Boolean }) isReadOnlyUser = /readonly/i.test(
    decryptValue(Cookies.get("userRole") || "")
  );
  @property({ type: String })
  loggedUserType = decryptValue(Cookies.get("userType") as string);
  @property({ type: Boolean }) isEnterpriseAdmin = /enterpriseadmin/i.test(
    decryptValue(Cookies.get("userType") || "")
  );
  @property({ type: String }) businessUnitName = "";
  @property({ type: String }) returnFilterEnvironment = "";
  @property({ type: String }) returnFilterDefault = "";

  get canManageInstitutions(): boolean {
    if (this.isReadOnlyUser) {
      return false;
    }
    if (this.isEnterpriseAdmin) {
      return true;
    }
    return this.loggedUserType?.toLowerCase() === UserTypes.businessadmin;
  }

  private handleInitialInstitutionFlagChange = (): void => {
    if (!isInitialInstitutionFeatureEnabled()) {
      if (this.apiLoading) {
        return;
      }
      showToastMessage(
        "Institution management is not available.",
        "danger"
      );
      AppRouters.go(this.institutionsListUrl());
      return;
    }
    this.requestUpdate();
  };

  private refreshFlagValues = (): void => {
    this.requestUpdate();
  };

  static readonly styles = css`
    #btnCon {
      display: flex;
      flex-direction: row;
      gap: 15px;
    }
    #addInstitution {
      padding: 24px;
      height: -webkit-fill-available;
      display: flex;
      flex-direction: column;
    }
    #breadcrumb {
      font-size: 14px;
      color: var(--jha-color-secondary-text);
      margin-bottom: 8px;
    }
    #breadcrumb a {
      color: var(--jha-color-primary);
      text-decoration: none;
      cursor: pointer;
    }
    #breadcrumb a:hover {
      text-decoration: underline;
    }
    h2 {
      margin-bottom: 18px;
      font-size: 24px;
      font-weight: 600;
    }
    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }
    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }
    jha-form-text-input {
      max-width: 410px;
    }
  `;

  private generateInstitutionUuid(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0")
      ).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    throw new Error("Secure random UUID generation is not available.");
  }

  private institutionsListUrl(): string {
    let url = `/business-units/${fixedEncodeURIComponent(
      this.businessUnitId
    )}/institutions`;
    const params: string[] = [];
    if (this.returnFilterEnvironment) {
      params.push(
        `environment=${encodeURIComponent(this.returnFilterEnvironment)}`
      );
    }
    if (this.returnFilterDefault) {
      params.push(
        `defaultStatus=${encodeURIComponent(this.returnFilterDefault)}`
      );
    }
    if (params.length) {
      url += `?${params.join("&")}`;
    }
    return url;
  }

  institutionDataChange() {
    const rtIdInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='institutionRtIdInput']"
    ) as HTMLInputElement;
    const environmentInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='institutionEnvironmentInput']"
    ) as HTMLInputElement;
    const consumerInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='consumerNameProductInput']"
    ) as HTMLInputElement;
    const checkbox = this.shadowRoot?.querySelector(
      "jha-form-checkbox[data-auto-id='isDefaultCheckbox']"
    ) as HTMLElement & { checked?: boolean };

    if (rtIdInput) {
      this.formData.institutionRtId = rtIdInput.value;
    }
    if (environmentInput) {
      this.formData.institutionEnvironment = environmentInput.value;
    }
    if (consumerInput) {
      this.formData.consumerNameProduct = consumerInput.value;
    }
    if (checkbox) {
      this.formData.isDefault = Boolean(checkbox.checked);
    }

    this.updateSubmitButtonState();
  }

  private updateSubmitButtonState() {
    const submitButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='submitAddInstitutionButton']"
    ) as HTMLButtonElement;

    if (this.apiLoading) {
      submitButton?.setAttribute("disabled", "");
    } else if (this.isFormComplete()) {
      submitButton?.removeAttribute("disabled");
    } else {
      submitButton?.setAttribute("disabled", "");
    }
  }

  isFormComplete(): boolean {
    return (
      String(this.formData?.institutionRtId ?? "").trim() !== "" &&
      String(this.formData?.institutionEnvironment ?? "").trim() !== "" &&
      String(this.formData?.consumerNameProduct ?? "").trim() !== ""
    );
  }

  regenerateInstitutionUuid() {
    this.formData = {
      ...this.formData,
      institutionUuid: this.generateInstitutionUuid(),
    };
    const uuidInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='institutionUuidInput']"
    ) as HTMLInputElement & { value?: string };
    if (uuidInput) {
      uuidInput.value = this.formData.institutionUuid;
      uuidInput.removeAttribute("warning");
    }
  }

  setSubmitButtonLoading(loading: boolean) {
    const addButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='submitAddInstitutionButton']"
    ) as HTMLButtonElement;
    if (loading) {
      addButton?.setAttribute("isLoading", "");
    } else {
      addButton?.removeAttribute("isLoading");
    }
  }

  clearButtonLoading() {
    this.setSubmitButtonLoading(false);
    const cancelButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='cancelAddInstitutionButton']"
    ) as HTMLButtonElement;
    cancelButton?.removeAttribute("isLoading");
  }

  private clearFormFields() {
    const root = this.shadowRoot;
    if (!root) {
      return;
    }
    for (const autoId of [
      "institutionRtIdInput",
      "institutionEnvironmentInput",
      "consumerNameProductInput",
    ]) {
      const el = root.querySelector(
        `jha-form-text-input[data-auto-id='${autoId}']`
      ) as HTMLInputElement & { value?: string };
      if (el) {
        el.value = "";
        el.removeAttribute("warning");
      }
    }
    const checkbox = root.querySelector(
      "jha-form-checkbox[data-auto-id='isDefaultCheckbox']"
    ) as HTMLElement & { checked?: boolean };
    if (checkbox) {
      checkbox.checked = false;
    }
    root
      .querySelector("jha-button[data-auto-id='submitAddInstitutionButton']")
      ?.setAttribute("disabled", "");
    this.requestUpdate();
  }

  private hasVisibleInlineFieldWarnings(): boolean {
    return Boolean(
      this.shadowRoot?.querySelector("jha-form-text-input[warning]")
    );
  }

  private getCheckboxWarningMessage(): string {
    return (
      this.shadowRoot
        ?.querySelector(
          "jha-form-checkbox[data-auto-id='isDefaultCheckbox'][warning]"
        )
        ?.getAttribute("warning") ?? ""
    ).trim();
  }

  private handleValidationErrorResponse(
    details: Array<Record<string, unknown>>
  ): void {
    let unmapped = "";
    if (this.shadowRoot) {
      unmapped = applyInstitutionFieldErrors(this.shadowRoot, details);
    } else {
      unmapped = getErrorMessage(details);
    }

    if (hasInstitutionUuidValidationError(details)) {
      this.regenerateInstitutionUuid();
      showToastMessage(
        "Institution UUID is already in use for this business unit. A new UUID was generated; review and submit again.",
        "danger"
      );
    } else if (unmapped) {
      showToastMessage(unmapped, "danger");
    } else {
      const checkboxWarning = this.getCheckboxWarningMessage();
      if (checkboxWarning) {
        showToastMessage(checkboxWarning, "danger");
      } else if (!this.hasVisibleInlineFieldWarnings()) {
        showToastMessage("Something went wrong, please try again.", "danger");
      }
    }
  }

  async onCancelClick() {
    if (this.apiLoading) {
      return;
    }

    const leave = () => {
      AppRouters.go(this.institutionsListUrl());
    };

    // Add starts empty (UUID is generated, not typed). isFormDirty is any
    // user-filled field. Read-only users cannot have changed anything.
    if (this.isReadOnlyUser || !this.isFormDirty()) {
      leave();
      return;
    }

    try {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave without saving?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Without Saving",
      });
      if (result === "confirm") {
        leave();
        return;
      }
    } finally {
      if (this.isConnected) {
        this.clearButtonLoading();
        this.apiLoading = false;
      }
    }
  }

  private liveInputValue(autoId: string, fallback: unknown): string {
    const host = this.shadowRoot?.querySelector(`[data-auto-id='${autoId}']`) as
      | (HTMLElement & { value?: unknown })
      | null
      | undefined;
    if (!host) return String(fallback ?? "");
    const inner = host.shadowRoot?.querySelector("input, textarea") as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null
      | undefined;
    if (inner && typeof inner.value === "string") return inner.value;
    if (typeof host.value === "string") return host.value;
    return String(fallback ?? "");
  }

  isFormDirty(): boolean {
    // Read the live controls — @change has not fired yet if Cancel is clicked
    // while a field still has focus.
    return (
      this.liveInputValue("institutionRtIdInput", this.formData?.institutionRtId).trim() !== "" ||
      this.liveInputValue("institutionEnvironmentInput", this.formData?.institutionEnvironment).trim() !== "" ||
      this.liveInputValue("consumerNameProductInput", this.formData?.consumerNameProduct).trim() !== "" ||
      Boolean(this.formData?.isDefault)
    );
  }

  validateFormData(formData: AddInstitutionType): boolean {
    if (!formData.institutionUuid) {
      showToastMessage("Institution UUID could not be generated.", "danger");
      return false;
    }
    if (formData.institutionRtId.trim() === "") {
      showToastMessage("RT ID is a required field.", "danger");
      return false;
    }
    if (formData.institutionEnvironment.trim() === "") {
      showToastMessage("Environment is a required field.", "danger");
      return false;
    }
    if (formData.consumerNameProduct.trim() === "") {
      showToastMessage("Consumer Name / Product is a required field.", "danger");
      return false;
    }
    if (!formData.businessUnitId) {
      showToastMessage("Business Unit is required.", "danger");
      return false;
    }
    return true;
  }

  trimFormDataValues(formData: AddInstitutionType): AddInstitutionType {
    return {
      ...formData,
      institutionRtId: formData.institutionRtId.trim(),
      institutionEnvironment: formData.institutionEnvironment.trim(),
      consumerNameProduct: formData.consumerNameProduct.trim(),
    };
  }

  async onAddInstitutionClick() {
    if (!isInitialInstitutionFeatureEnabled()) {
      showToastMessage(
        "Institution management is not available.",
        "danger"
      );
      AppRouters.go(this.institutionsListUrl());
      return;
    }
    if (!this.canManageInstitutions) {
      showToastMessage("You do not have permission to create institutions.", "danger");
      return;
    }
    if (this.apiLoading) {
      return;
    }

    this.apiLoading = true;
    this.institutionDataChange();
    const trimmed = this.trimFormDataValues(this.formData);
    this.formData = trimmed;

    if (!this.validateFormData(trimmed)) {
      this.apiLoading = false;
      this.clearButtonLoading();
      return;
    }

    try {
      this.setSubmitButtonLoading(true);

      const response = await postRequest<AddInstitutionType>(
        Apis.INSTITUTION_ADD,
        trimmed
      );

      if (!response) {
        return;
      }

      if (
        response instanceof Response ||
        response?.status === StatusConstants.UNAUTHORIZED_USER_CODE ||
        response?.status === StatusConstants.NOT_FOUND_USER_CODE
      ) {
        const status =
          response instanceof Response ? response.status : response?.status;
        if (status === StatusConstants.UNAUTHORIZED_USER_CODE) {
          showToastMessage(
            "You do not have permission to create institutions.",
            "danger"
          );
        } else if (status === StatusConstants.NOT_FOUND_USER_CODE) {
          showToastMessage("Business Unit not found.", "danger");
        } else {
          showToastMessage("Something went wrong, please try again.", "danger");
        }
        return;
      }

      if (response?.messageStatus === null) {
        showToastMessage("Institution created successfully.", "success");
        requestInstitutionsListRefresh(this.businessUnitId);
        AppRouters.go(this.institutionsListUrl());
        return;
      }

      if (response?.details?.length > 0) {
        this.handleValidationErrorResponse(response.details);
        return;
      }

      const errMsg = getErrorMessage(response?.details);
      showToastMessage(
        errMsg.trim() || "Something went wrong, please try again.",
        "danger"
      );
    } catch {
      showToastMessage("Something went wrong, please try again.", "danger");
    } finally {
      this.apiLoading = false;
      this.clearButtonLoading();
      this.updateSubmitButtonState();
    }
  }

  private async verifyBusinessUnitAccess(): Promise<boolean> {
    try {
      const buData = await getRequest(
        Apis.GET_BUSINESSUNIT +
        `/${fixedEncodeURIComponent(this.businessUnitId)}`
      );

      if (!buData) {
        showToastMessage("Something went wrong, please try again.", "danger");
        AppRouters.go("/business-units");
        return false;
      }

      if (buData instanceof Response) {
        if (buData.status === StatusConstants.UNAUTHORIZED_USER_CODE) {
          showToastMessage(
            "You do not have permission to manage this Business Unit.",
            "danger"
          );
        } else if (buData.status === StatusConstants.NOT_FOUND_USER_CODE) {
          showToastMessage("Business Unit not found.", "danger");
        } else {
          showToastMessage("Something went wrong, please try again.", "danger");
        }
        AppRouters.go("/business-units");
        return false;
      }

      if (
        this.loggedUserType?.toLowerCase() === UserTypes.businessadmin &&
        !isSessionBusinessUnitAllowed(buData)
      ) {
        showToastMessage(
          "You do not have permission to manage this Business Unit.",
          "danger"
        );
        AppRouters.go("/business-units");
        return false;
      }

      this.businessUnitName =
        buData.businessUnitName ?? buData.BusinessUnitName ?? "";
      return true;
    } catch {
      showToastMessage("Something went wrong, please try again.", "danger");
      AppRouters.go("/business-units");
      return false;
    }
  }

  async routeEnter(
    currentNode: unknown,
    nextNodeIfExists: unknown,
    routeId: string,
    context: unknown
  ) {
    await super.routeEnter(currentNode, nextNodeIfExists, routeId, context);
    if (!nextNodeIfExists) {
      const params = new URLSearchParams(window.location.search);
      this.returnFilterEnvironment = params.get("environment") ?? "";
      const defaultParam = params.get("defaultStatus") ?? "";
      this.returnFilterDefault =
        defaultParam === "true" || defaultParam === "false"
          ? defaultParam
          : "";

      await launchDarkly.whenReady();
      if (!isInitialInstitutionFeatureEnabled()) {
        showToastMessage(
          "Institution management is not available.",
          "danger"
        );
        AppRouters.go(this.institutionsListUrl());
        return;
      }

      if (!this.canManageInstitutions) {
        showToastMessage(
          "You do not have permission to create institutions.",
          "danger"
        );
        AppRouters.go(this.institutionsListUrl());
        return;
      }

      const allowed = await this.verifyBusinessUnitAccess();
      if (!allowed) {
        return;
      }

      this.formData = {
        ...this.formData,
        institutionUuid: this.generateInstitutionUuid(),
        businessUnitId: this.businessUnitId,
        institutionRtId: "",
        institutionEnvironment: "",
        consumerNameProduct: "",
        isDefault: false,
        createdBy: decryptValue(Cookies.get("userId") || ""),
      };
      await this.updateComplete;
      this.clearFormFields();
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    this.refreshFlagValues();
    launchDarkly.whenReady().then(() => this.refreshFlagValues());
    launchDarkly.on(FeatureFlags.INITIAL_INSTITUTION, this.handleInitialInstitutionFlagChange);
    document.title = GlobalVariables.MAIN_TITLE?.concat(
      " > Add Institution"
    );
  }

  disconnectedCallback() {
    launchDarkly.off(FeatureFlags.INITIAL_INSTITUTION, this.handleInitialInstitutionFlagChange);
    super.disconnectedCallback();
  }

  onBackToInstitutions() {
    if (this.apiLoading) {
      return;
    }
    AppRouters.go(this.institutionsListUrl());
  }

  render() {
    return html`
      <div id="addInstitution">
        <div id="breadcrumb">
          <a @click=${this.onBackToBusinessUnits}>Business Units</a>
          &rsaquo;
          <a @click=${this.onBackToInstitutions}
            >${this.businessUnitName || this.businessUnitId}</a
          >
          &rsaquo; Institutions &rsaquo; Create
        </div>
        <h1>Create Institution</h1>

        <div>
          <h2>Institution Information</h2>
          <div style="max-width: 410px;">
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="institutionUuidInput"
                label="Institution UUID"
                outline
                readonly
                .value=${this.formData.institutionUuid}
              >
              </jha-form-text-input>
            </jha-form-row>
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="institutionRtIdInput"
                label="RT ID"
                required
                outline
                minlength="1"
                maxlength="256"
                warning="RT ID is a required field."
                .value=${this.formData.institutionRtId}
                ?disabled=${this.apiLoading}
                @input=${this.institutionDataChange}
                @change=${this.institutionDataChange}
              >
              </jha-form-text-input>
            </jha-form-row>
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="institutionEnvironmentInput"
                label="Environment"
                required
                outline
                minlength="1"
                maxlength="256"
                warning="Environment is a required field."
                .value=${this.formData.institutionEnvironment}
                ?disabled=${this.apiLoading}
                @input=${this.institutionDataChange}
                @change=${this.institutionDataChange}
              >
              </jha-form-text-input>
            </jha-form-row>
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="consumerNameProductInput"
                label="Consumer Name / Product"
                required
                outline
                minlength="1"
                maxlength="256"
                warning="Consumer Name / Product is a required field."
                .value=${this.formData.consumerNameProduct}
                ?disabled=${this.apiLoading}
                @input=${this.institutionDataChange}
                @change=${this.institutionDataChange}
              >
              </jha-form-text-input>
            </jha-form-row>
            <jha-form-row>
              <jha-form-checkbox
                data-auto-id="isDefaultCheckbox"
                ?checked=${this.formData.isDefault}
                ?disabled=${this.isReadOnlyUser || this.apiLoading}
                @change=${this.institutionDataChange}
              >
                Set as default institution
              </jha-form-checkbox>
            </jha-form-row>
          </div>
        </div>

        <div style="flex:1;"></div>
        <div id="btnCon">
          <jha-button
            data-auto-id="submitAddInstitutionButton"
            type="button"
            large
            ?disabled=${this.apiLoading || !this.isFormComplete()}
            @click=${this.onAddInstitutionClick}
          >
            Create Institution
          </jha-button>
          <jha-button
            data-auto-id="cancelAddInstitutionButton"
            type="button"
            tertiary
            large
            ?disabled=${this.apiLoading}
            @click=${this.onCancelClick}
          >
            Cancel
          </jha-button>
        </div>
      </div>
    `;
  }

  onBackToBusinessUnits() {
    if (this.apiLoading) {
      return;
    }
    AppRouters.go("/business-units");
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-institution": AddInstitution;
  }
}
